import React, {
  useCallback,
  useContext,
  useState,
  useMemo,
  useEffect,
} from 'react';
import format from 'string-template';
import { observer } from 'mobx-react';
import { compose } from 'recompose';
import * as Sentry from '@sentry/browser';
import { forEachSeries } from 'p-iteration';
import { useRouteMatch } from 'react-router-dom';

import CopyAllIcon from '@mui/icons-material/CopyAll';
import SaveIcon from '@mui/icons-material/Save';

import { pathPlaceholder, storagePaths } from '../../../../../utils/firebasePaths';
import useStorage, { StorageProcessState } from '../../../../../hooks/useStorage';
import { usePostHog } from '../../../../../chat/hooks/usePostHog';
import useComponentMounted from '../../../../../hooks/useComponentMounted';
import useLogger from '../../../../../hooks/useLogger';
import { CoachingActivity } from '../../../../../utils/log';
import CustomizationContext, { withCustomizationContextReady } from '../../../../context/CustomizationContext';
import useToast from '../../../../hooks/useToast';
import { WELCOME_MESSAGE_PLACEHOLDER } from '../../../../utils/appCustomization';
import AttachmentsContainer from '../../../../components/AttachmentsContainer';
import LoadingOverlay from '../../../../components/LoadingOverlay';
import { MAX_FILE_UPLOAD_SIZE, MAX_FILE_UPLOAD_SIZE_MB } from '../../../../../utils/files';
import useAttachmentInputHandlers from '../../../../../hooks/useAttachmentInputHandlers';

import commonTexts from '../texts.json';
import {
  Title,
  Description,
} from '../../styles';
import {
  PlaceholderButton,
  PlaceholdersContainer,
  PlaceholderTip,
} from '../../../../components/PlaceHolder/styles';
import {
  ContentWrapper,
  InputContainer,
  StyledTextField,
  StyledButton,
  WelcomeMessageContainer,
} from './styles';

import screenTexts from './texts.json';

const MAX_CHARACTERS_COUNT = 5000;

const texts = {
  ...commonTexts,
  ...screenTexts,
};

const WelcomeMessageConfig = () => {
  const {
    appCustomizationDoc,
  } = useContext(CustomizationContext);

  const {
    welcomeMessage: customMessage,
  } = appCustomizationDoc;
  const { params: { userId: coachId } } = useRouteMatch();
  const { isAIFriendEnabled } = usePostHog();

  const {
    uploadAttachments,
    deleteAttachments,
    getBlobUrl,
  } = useStorage();
  const isComponentMountedRef = useComponentMounted();

  const [welcomeMessage, setWelcomeMessage] = useState(customMessage);
  const [characterCount, setCharacterCount] = useState(customMessage.length);
  const [fileAttachments, setFileAttachments] = useState([]); // Array of Files
  const [isUploading, setIsUploading] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const [aiFriendFileAttachments, setAIFriendFileAttachments] = useState([]);

  const { showToast } = useToast();
  const { logCoachingActivity } = useLogger();

  const { onFilesSelected, removeFileByIndex, copyPlaceholderValue } = useAttachmentInputHandlers({
    showToast,
    fileSizeErrorText: texts.fileSizeError,
    maxFileSizeBytes: MAX_FILE_UPLOAD_SIZE,
    maxFileSizeMb: MAX_FILE_UPLOAD_SIZE_MB,
    placeholderCopiedText: texts.placeholderCopied,
  });

  useEffect(() => {
    const init = async () => {
      const files = [];
      await forEachSeries(appCustomizationDoc.welcomeMessageAttachments, async (attachment) => {
        const dataUrl = await getBlobUrl(attachment.url);
        files.push({
          ...attachment,
          dataUrl,
          isUploaded: true,
        });
      });
      const aiFiles = [];
      await forEachSeries(appCustomizationDoc.aiWelcomeMessageAttachments, async (attachment) => {
        const dataUrl = await getBlobUrl(attachment.url);
        aiFiles.push({
          ...attachment,
          dataUrl,
          isUploaded: true,
        });
      });
      if (isComponentMountedRef.current) {
        setFileAttachments(files);
        setAIFriendFileAttachments(aiFiles);
        setIsReady(true);
      }
    };

    if (!isReady) {
      init();
    }
  }, [
    getBlobUrl,
    appCustomizationDoc.welcomeMessageAttachments,
    fileAttachments,
    appCustomizationDoc.aiWelcomeMessageAttachments,
    isReady,
    isComponentMountedRef,
  ]);

  const uploadMessageAttachments = useCallback(async (attachments) => {
    // adding already uploaded files to the array
    const attachmentsRefs = attachments.reduce((arr, current) => {
      if (current.isUploaded) {
        arr.push({
          name: current.name,
          url: current.url,
        });
      }
      return arr;
    }, []);

    // filtering files that are not uploaded yet
    const filesToUpload = attachments.filter((file) => !file.isUploaded);
    const storagePath = format(storagePaths.COACH_WELCOME_MESSAGE_ATTACHMENTS, {
      [pathPlaceholder.COACH_ID]: coachId,
    });

    setIsUploading(true);
    try {
      const result = await uploadAttachments(filesToUpload, storagePath);

      let storageTaskFailed = false;
      const successfulUploads = [];
      result.forEach((res, index) => {
        if (res.state === StorageProcessState.SUCCESS) {
          successfulUploads.push({
            name: filesToUpload[index].name,
            url: res.fileRef,
          });
        } else {
          storageTaskFailed = true;
        }
      });
      if (storageTaskFailed) {
        // If some upload fails, we should stop the saving process
        // and delete the files that were uploaded (if there is any)
        if (attachmentsRefs.length) {
          const filesToDelete = successfulUploads.map(({ url }) => ({ storagePath: url }));
          await deleteAttachments(filesToDelete);
        }
      }
      attachmentsRefs.push(...successfulUploads);
      if (isComponentMountedRef.current) {
        setIsUploading(false);
      }
      return { attachmentsRefs, storageTaskFailed };
    } catch (error) {
      if (isComponentMountedRef.current) {
        setIsUploading(false);
      }
      Sentry.captureException(error, {
        extra: {
          coachId,
        },
      });
      // Returning this to handle error messages from the caller function
      return { storageTaskFailed: true };
    }
  }, [
    coachId,
    uploadAttachments,
    deleteAttachments,
    isComponentMountedRef,
  ]);

  const uploadAIFriendAttachments = useCallback(async () => {
    const { storageTaskFailed, attachmentsRefs } = await uploadMessageAttachments(aiFriendFileAttachments);
    return {
      error: !!storageTaskFailed,
      attachments: attachmentsRefs,
    };
  }, [
    aiFriendFileAttachments,
    uploadMessageAttachments,
  ]);

  const getAttachmentRefs = useCallback(async () => {
    let attachments = [];
    let error = false;
    if (fileAttachments.length) {
      const { storageTaskFailed, attachmentsRefs } = await uploadMessageAttachments(fileAttachments);
      if (storageTaskFailed) {
        error = true;
      }
      attachments = attachmentsRefs;
    }
    return {
      attachments,
      error,
    };
  }, [
    fileAttachments,
    uploadMessageAttachments,
  ]);

  const handleSave = useCallback(async () => {
    const { attachments, error } = await getAttachmentRefs();
    if (error) {
      showToast(texts.uploadError, { error: true });
      return;
    }
    await appCustomizationDoc.setWelcomeMessage(welcomeMessage);
    await appCustomizationDoc.setWelcomeMessageAttachments(attachments);
    logCoachingActivity(CoachingActivity.UPDATED_WELCOME_MESSAGE);
    setIsReady(false);
    showToast(texts.messageSaved);
  }, [
    appCustomizationDoc,
    welcomeMessage,
    showToast,
    getAttachmentRefs,
    logCoachingActivity,
  ]);

  const handleSaveAIFriends = useCallback(async () => {
    const { attachments, error } = await uploadAIFriendAttachments();
    if (error) {
      showToast(texts.uploadError, { error: true });
      return;
    }
    await appCustomizationDoc.setAIWelcomeMessageAttachments(attachments);
    setIsReady(false);
    showToast(texts.aiFriendSaved);
  }, [
    appCustomizationDoc,
    showToast,
    uploadAIFriendAttachments,
  ]);

  const onAIFilesSelected = useCallback((event) => {
    onFilesSelected(event, setAIFriendFileAttachments);
  }, [
    onFilesSelected,
  ]);

  const removeAIFriendFileByIndex = useCallback((index) => {
    removeFileByIndex(index, setAIFriendFileAttachments);
  }, [removeFileByIndex]);

  const handleTextChange = useCallback((event) => {
    const newValue = event.target.value;
    setWelcomeMessage(newValue);
    setCharacterCount(newValue.length);
  }, []);

  const isError = useMemo(() => characterCount > MAX_CHARACTERS_COUNT, [characterCount]);

  return (
    <ContentWrapper>
      <Title>{texts.title}</Title>
      <Description>{texts.description}</Description>
      <WelcomeMessageContainer>
        <InputContainer>
          <StyledTextField
            value={welcomeMessage}
            onChange={handleTextChange}
            placeholder={texts.defaultMessage}
            error={isError}
            helperText={format(texts.characterCount, {
              count: characterCount,
              maxCount: MAX_CHARACTERS_COUNT,
            })}
            multiline
            maxRows={18}
          />
          <AttachmentsContainer
            fileAttachments={fileAttachments}
            onFileRemoved={(index) => removeFileByIndex(index, setFileAttachments)}
            onFilesSelected={(event) => onFilesSelected(event, setFileAttachments)}
            isUploading={isUploading}
          />
          <StyledButton
            variant="contained"
            onClick={handleSave}
            disabled={isError}
            startIcon={<SaveIcon />}
          >
            {texts.saveButton}
          </StyledButton>
        </InputContainer>
        <PlaceholdersContainer>
          <PlaceholderTip>{texts.placeholderTitle}</PlaceholderTip>
          {Object.values(WELCOME_MESSAGE_PLACEHOLDER).map((placeholderValue) => {
            const displayText = `{${placeholderValue}}`;
            return (
              <PlaceholderButton
                key={placeholderValue}
                startIcon={<CopyAllIcon />}
                onClick={() => copyPlaceholderValue(placeholderValue)}
              >
                {displayText}
              </PlaceholderButton>
            );
          })}
          <PlaceholderTip>{texts.placeholderDescription}</PlaceholderTip>
        </PlaceholdersContainer>
      </WelcomeMessageContainer>
      {isAIFriendEnabled() && (
        <>
          <Title>{texts.aiFriendSectionTitle}</Title>
          <Description>{texts.aiFriendSectionDescription}</Description>
          <InputContainer>
            <AttachmentsContainer
              fileAttachments={aiFriendFileAttachments}
              onFileRemoved={removeAIFriendFileByIndex}
              onFilesSelected={onAIFilesSelected}
              isUploading={isUploading}
            />
            <StyledButton
              variant="contained"
              onClick={handleSaveAIFriends}
              startIcon={<SaveIcon />}
            >
              {texts.saveButton}
            </StyledButton>
          </InputContainer>
        </>
      )}
      <LoadingOverlay isLoading={!isReady} />
    </ContentWrapper>
  );
};

export default compose(
  withCustomizationContextReady,
  observer,
)(WelcomeMessageConfig);
